import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
  PARTNER_BANK_JOURNEY_STAGES,
  PartnerBankJourneyStage,
} from '@interfaces/partners/responses/partner-bank-journey.response';
import { KanbanColumnTotal } from '@shared/components/kanban-board/kanban-board.types';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  BANK_JOURNEY_STAGE_TONES,
  BankJourneyStageTone,
  bankJourneyTotalsByCurrency,
} from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/bank-journey-totals';
import { DealBankJourneyStore } from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/deal-bank-journey.store';

/** Uma caixa do resumo: a etapa, quantos bancos estão nela e o valor por moeda. */
interface StageSummary {
  stage: PartnerBankJourneyStage;
  label: string;
  tone: BankJourneyStageTone;
  count: number;
  totals: KanbanColumnTotal[];
}

/**
 * Resumo da **Jornada nos bancos** no cabeçalho do deal (card 3752): no modo bancos ele ocupa o lugar do stepper da
 * Jornada do deal. Uma caixa por etapa do banco, na ordem das colunas do quadro, com a quantidade e o valor por
 * moeda (a mesma soma da coluna do quadro, `bankJourneyTotalsByCurrency`).
 */
@Component({
  selector: 'app-deal-bank-journey-summary',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './deal-bank-journey-summary.component.html',
  styleUrl: './deal-bank-journey-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealBankJourneySummaryComponent {
  protected readonly store = inject(DealBankJourneyStore);

  /** Título do resumo; o Meus deals (card 3765) diz "Jornada nos bancos · todos os deals". */
  readonly heading = input('Jornada nos bancos');
  /** Mostra quantos deals os bancos somam (Meus deals, card 3765). */
  readonly showDealCount = input(false);

  protected readonly dealCount = computed(
    () => new Set(this.store.cards().map(card => card.partnerDealId)).size
  );

  protected readonly stages = computed<StageSummary[]>(() => {
    const cards = this.store.cards();
    return PARTNER_BANK_JOURNEY_STAGES.map(({ value, label }) => {
      const inStage = cards.filter(card => card.stage === value);
      return {
        stage: value,
        label,
        tone: BANK_JOURNEY_STAGE_TONES[value],
        count: inStage.length,
        totals: bankJourneyTotalsByCurrency(inStage),
      };
    });
  });

  /** Aprovados = já passaram pela aprovação (Aprovado e Desembolso). */
  protected readonly approvedCount = computed(
    () =>
      this.store.cards().filter(card => card.stage === 'Approved' || card.stage === 'Disbursement')
        .length
  );
}
